import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ShieldCheck, ShieldAlert, X, Mail, MessageSquare, Check, AlertTriangle, Plus } from 'lucide-react';
import { getOutboundSettings, updateOutboundSettings } from '../../../data/api';
import { Loading, ErrorFallback } from './CiShared';
import './safetySettings.css';

// Send-Safety settings editor (modal). Reads/writes the SAME outbound settings
// document that the live safety gate enforces at send time
// (previewOutboundEmail/Sms -> sendEmails/sendSms validate against send_mode).
// Editing here only changes how that gate behaves — it never bypasses it.
//
// Backed fields (see backend OutboundSettingsUpdate):
//   safety_enabled        bool
//   send_mode             'hold' | 'test' | 'live'
//   test_email_recipients string[]
//   test_sms_recipients   string[]
//   sms_provider_enabled  bool
//
// getOutboundSettings() / updateOutboundSettings() both return the normalized
// settings object, so we re-seed local state from the server's response on save.

const MODES = [
  {
    key: 'hold',
    label: 'Hold for Review',
    desc: 'Nothing goes out automatically. Every message is queued in the Outbox for you to review and release.',
    tone: 'blue',
  },
  {
    key: 'test',
    label: 'Test Mode',
    desc: 'Sends are recorded against your test recipients only — real guests are never contacted.',
    tone: 'green',
  },
  {
    key: 'live',
    label: 'Live',
    desc: 'Confirmed messages are delivered to real recipients. Use once you have reviewed your setup.',
    tone: 'gold',
  },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Loose phone check: at least 7 digits once symbols are stripped. The backend is
// the source of truth for normalization — this only guards obvious typos.
const phoneLooksValid = (v) => (String(v || '').replace(/[^0-9]/g, '').length >= 7);

function normalizeFromServer(s) {
  return {
    safety_enabled: !!s?.safety_enabled,
    send_mode: ['hold', 'test', 'live'].includes(s?.send_mode) ? s.send_mode : 'hold',
    test_email_recipients: Array.isArray(s?.test_email_recipients) ? s.test_email_recipients : [],
    test_sms_recipients: Array.isArray(s?.test_sms_recipients) ? s.test_sms_recipients : [],
    sms_provider_enabled: !!s?.sms_provider_enabled,
  };
}

function sameSettings(a, b) {
  if (!a || !b) return false;
  return (
    a.safety_enabled === b.safety_enabled &&
    a.send_mode === b.send_mode &&
    a.sms_provider_enabled === b.sms_provider_enabled &&
    a.test_email_recipients.join('') === b.test_email_recipients.join('') &&
    a.test_sms_recipients.join('') === b.test_sms_recipients.join('')
  );
}

// Reusable on/off switch styled like the module's Milo switch.
function Toggle({ checked, onChange, disabled, labelOn, labelOff }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      className={`ss-switch ${checked ? 'ss-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="ss-switch-track" aria-hidden="true"><span className="ss-switch-knob" /></span>
      <span className="ss-switch-lbl">{checked ? labelOn : labelOff}</span>
    </button>
  );
}

// Tag-style recipient editor. Add via Enter / comma / the + button; remove via
// the × on each chip. Validation is advisory (invalid entries are flagged but
// the backend still normalizes/dedupes on save).
function RecipientEditor({ icon, kind, values, onChange, placeholder, validate, disabled }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  const commit = (raw) => {
    const parts = String(raw)
      .split(/[,\n;]+/)
      .map((p) => p.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = [...values];
    parts.forEach((p) => { if (!next.some((v) => v.toLowerCase() === p.toLowerCase())) next.push(p); });
    onChange(next);
    setDraft('');
  };

  const removeAt = (i) => onChange(values.filter((_, idx) => idx !== i));

  return (
    <div className={`ss-recip ${disabled ? 'ss-recip-dim' : ''}`}>
      <div className="ss-chips">
        {values.length === 0 && <span className="ss-chips-empty">No {kind} recipients yet.</span>}
        {values.map((v, i) => {
          const ok = !validate || validate(v);
          return (
            <span key={`${v}-${i}`} className={`ss-chip ${ok ? '' : 'ss-chip-bad'}`} title={ok ? v : `${v} — this doesn't look valid`}>
              {!ok && <AlertTriangle size={11} />}
              {v}
              <button type="button" className="ss-chip-x" aria-label={`Remove ${v}`} onClick={() => removeAt(i)} disabled={disabled}><X size={11} /></button>
            </span>
          );
        })}
      </div>
      <div className="ss-recip-row">
        <span className="ss-recip-ic">{icon}</span>
        <input
          ref={inputRef}
          className="ss-input"
          value={draft}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(draft); }
            else if (e.key === 'Backspace' && !draft && values.length) { removeAt(values.length - 1); }
          }}
          onBlur={() => { if (draft.trim()) commit(draft); }}
        />
        <button
          type="button"
          className="ci-btn ci-sm ss-add"
          onClick={() => { commit(draft); inputRef.current?.focus(); }}
          disabled={disabled || !draft.trim()}
          title="Add recipient"
        >
          <Plus size={13} /> Add
        </button>
      </div>
    </div>
  );
}

export default function SafetySettingsModal({ open, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState(null);
  const [serverForm, setServerForm] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const s = normalizeFromServer(await getOutboundSettings());
      setForm(s);
      setServerForm(s);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || e?.message || 'Could not load your send settings.');
    } finally {
      setLoading(false);
    }
  };

  // (Re)load whenever the modal is opened so it always reflects current state.
  useEffect(() => {
    if (!open) return;
    setSaved(false);
    setSaveError('');
    load();
  }, [open]);

  // Close on Escape for keyboard users.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const patch = (next) => { setForm((f) => ({ ...f, ...next })); setSaved(false); setSaveError(''); };

  const dirty = useMemo(() => !sameSettings(form, serverForm), [form, serverForm]);

  // Guard rails so the host can't save a config the gate would treat as a dead
  // end (Test Mode with no test recipients on an enabled channel).
  const warnings = useMemo(() => {
    if (!form) return [];
    const out = [];
    if (form.safety_enabled && form.send_mode === 'test' && form.test_email_recipients.length === 0) {
      out.push('Test Mode is selected but you have no test email recipients — email test sends will have nowhere to go.');
    }
    if (form.safety_enabled && form.send_mode === 'test' && form.sms_provider_enabled && form.test_sms_recipients.length === 0) {
      out.push('Test Mode is selected and SMS is on, but you have no test SMS recipients.');
    }
    if (!form.safety_enabled) {
      out.push('The safety layer is OFF. Sends follow the selected mode without the extra guardrail — turn it on for the protected workflow.');
    }
    return out;
  }, [form]);

  const handleSave = async () => {
    if (!form || saving) return;
    setSaving(true);
    setSaveError('');
    setSaved(false);
    try {
      const payload = {
        safety_enabled: form.safety_enabled,
        send_mode: form.send_mode,
        test_email_recipients: form.test_email_recipients,
        test_sms_recipients: form.test_sms_recipients,
        sms_provider_enabled: form.sms_provider_enabled,
      };
      const s = normalizeFromServer(await updateOutboundSettings(payload));
      setForm(s);
      setServerForm(s);
      setSaved(true);
      onSaved?.(s);
    } catch (e) {
      setSaveError(e?.response?.data?.detail || e?.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="ss-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Send safety settings"
      onClick={() => { if (!saving) onClose?.(); }}
    >
      <div className="ci-glass ss-panel" onClick={(e) => e.stopPropagation()}>
        {/* header */}
        <div className="ss-head">
          <div className="ss-head-ttl">
            <span className={`ss-head-ic ${form && !form.safety_enabled ? 'ss-warn' : ''}`}>
              {form && !form.safety_enabled ? <ShieldAlert size={18} /> : <ShieldCheck size={18} />}
            </span>
            <div>
              <h2>Send Safety</h2>
              <p>Control how Milo and your campaigns reach real guests.</p>
            </div>
          </div>
          <button type="button" className="ss-close" aria-label="Close" onClick={() => { if (!saving) onClose?.(); }}><X size={18} /></button>
        </div>

        {/* body */}
        <div className="ss-body">
          {loading && <Loading label="Loading your send settings…" />}
          {!loading && loadError && <ErrorFallback message={loadError} onRetry={load} />}

          {!loading && !loadError && form && (
            <>
              {/* Safety layer master switch */}
              <section className="ss-sec">
                <div className="ss-sec-row">
                  <div>
                    <div className="ss-sec-h">Safety layer</div>
                    <div className="ss-sec-sub">The guardrail that validates every outbound batch before it leaves. Recommended ON.</div>
                  </div>
                  <Toggle
                    checked={form.safety_enabled}
                    onChange={(v) => patch({ safety_enabled: v })}
                    disabled={saving}
                    labelOn="On"
                    labelOff="Off"
                  />
                </div>
              </section>

              {/* Send mode */}
              <section className="ss-sec">
                <div className="ss-sec-h">Send mode</div>
                <div className="ss-sec-sub">What happens when a message is confirmed for sending.</div>
                <div className="ss-modes">
                  {MODES.map((m) => {
                    const active = form.send_mode === m.key;
                    return (
                      <button
                        type="button"
                        key={m.key}
                        className={`ss-mode ss-tone-${m.tone} ${active ? 'ss-mode-on' : ''}`}
                        aria-pressed={active}
                        onClick={() => patch({ send_mode: m.key })}
                        disabled={saving}
                      >
                        <div className="ss-mode-top">
                          <span className="ss-mode-dot" />
                          <span className="ss-mode-name">{m.label}</span>
                          {active && <Check size={14} className="ss-mode-check" />}
                        </div>
                        <div className="ss-mode-desc">{m.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </section>

              {/* Test recipients */}
              <section className="ss-sec">
                <div className="ss-sec-h">Test recipients</div>
                <div className="ss-sec-sub">
                  Where Test Mode delivers instead of real guests. {form.send_mode !== 'test' && <span className="ss-muted">(used when Test Mode is on)</span>}
                </div>
                <div className="ss-field">
                  <label className="ss-lbl">Email</label>
                  <RecipientEditor
                    icon={<Mail size={14} />}
                    kind="email"
                    values={form.test_email_recipients}
                    onChange={(v) => patch({ test_email_recipients: v })}
                    placeholder="you@example.com — press Enter"
                    validate={(v) => EMAIL_RE.test(v)}
                    disabled={saving}
                  />
                </div>
                <div className="ss-field">
                  <label className="ss-lbl">SMS</label>
                  <RecipientEditor
                    icon={<MessageSquare size={14} />}
                    kind="SMS"
                    values={form.test_sms_recipients}
                    onChange={(v) => patch({ test_sms_recipients: v })}
                    placeholder="+15555550123 — press Enter"
                    validate={phoneLooksValid}
                    disabled={saving}
                  />
                </div>
              </section>

              {/* SMS provider */}
              <section className="ss-sec">
                <div className="ss-sec-row">
                  <div>
                    <div className="ss-sec-h">SMS channel</div>
                    <div className="ss-sec-sub">
                      Turn on to allow text-message sends. Carrier credentials live in your server environment;
                      this switch only enables the channel in the safety gate.
                    </div>
                  </div>
                  <Toggle
                    checked={form.sms_provider_enabled}
                    onChange={(v) => patch({ sms_provider_enabled: v })}
                    disabled={saving}
                    labelOn="On"
                    labelOff="Off"
                  />
                </div>
              </section>

              {/* advisory warnings */}
              {warnings.length > 0 && (
                <div className="ss-warns" role="note">
                  {warnings.map((w, i) => (
                    <div key={i} className="ss-warn-row"><AlertTriangle size={14} /><span>{w}</span></div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* footer */}
        {!loading && !loadError && form && (
          <div className="ss-foot">
            <div className="ss-foot-msg" aria-live="polite">
              {saveError && <span className="ss-foot-err"><AlertTriangle size={13} /> {saveError}</span>}
              {!saveError && saved && !dirty && <span className="ss-foot-ok"><Check size={13} /> Saved</span>}
              {!saveError && dirty && <span className="ss-foot-dirty">Unsaved changes</span>}
            </div>
            <div className="ss-foot-actions">
              <button type="button" className="ci-btn ci-sm" onClick={() => { if (!saving) onClose?.(); }} disabled={saving}>
                {dirty ? 'Cancel' : 'Close'}
              </button>
              <button
                type="button"
                className="ci-btn ci-gold ci-sm"
                onClick={handleSave}
                disabled={saving || !dirty}
                title={!dirty ? 'No changes to save' : 'Save send settings'}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
